import type { LabelRateRow } from './carrierRateUtils'
import { carriersMatch, getServiceOptionsFromRates, normalizeCarrierName } from './carrierRateUtils'

/** Coerce API/mock EasyPost rate rows into a stable shape for Redux + UI. */
export function normalizeLabelRates(payload: unknown): LabelRateRow[] {
  if (!Array.isArray(payload)) return []
  const rows: LabelRateRow[] = []
  for (const raw of payload) {
    if (!raw || typeof raw !== 'object') continue
    const row = raw as Record<string, unknown>
    const service = String(row.service ?? '').trim()
    const carrier = String(row.carrier ?? '').trim()
    if (!service || !carrier) continue
    const rateNum = Number(row.rate)
    rows.push({
      ...(row as LabelRateRow),
      carrier,
      service,
      rate: Number.isFinite(rateNum) ? rateNum : undefined,
    })
  }
  return rows
}

export function coercePostageFromRateRow(row: LabelRateRow | null | undefined): number | null {
  if (!row || row.rate == null) return null
  const n = Number(row.rate)
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.round(n * 100) / 100
}

/** Match selected carrier + service against returned rate rows (mock + live). */
export function findSelectedLabelRate(
  rates: unknown,
  shippingMethod: string,
  shippingService: string,
): LabelRateRow | null {
  const ratesArray = normalizeLabelRates(rates)
  if (!ratesArray.length || !shippingService.trim()) return null
  const match = ratesArray.find(
    (rate) =>
      carriersMatch(rate.carrier, shippingMethod) &&
      String(rate.service ?? '').trim() === String(shippingService).trim(),
  )
  return match ?? null
}

/** First service option for carrier when prefs/service do not match returned rates. */
export function firstServiceNameForCarrier(
  rates: unknown,
  shippingMethod: string,
): string | null {
  const options = getServiceOptionsFromRates(normalizeLabelRates(rates), shippingMethod)
  if (!options.length) return null
  const raw = options[0].value
  const colon = raw.indexOf(':')
  return colon === -1 ? raw : raw.slice(colon + 1)
}

export function serviceMatchesReturnedRates(
  rates: unknown,
  shippingMethod: string,
  shippingService: string,
): boolean {
  if (!normalizeCarrierName(shippingMethod) || !shippingService.trim()) return false
  return findSelectedLabelRate(rates, shippingMethod, shippingService) != null
}
